import React, { useState } from "react";
import {
  Box,
  TextField,
  Tabs,
  Tab,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { TabContext, TabList, TabPanel } from "@mui/lab";

const InterviewQuestionSection = () => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState("advanced");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const questionsPerPage = 5;

  // Static data for homepage - Java Interview Questions
  const staticQuestions = {
    advanced: [
      {
        question: "What is a lambda expression in Java?",
        answer:
          "A lambda expression is a concise way to represent an anonymous function in Java.",
      },
      {
        question:
          "Explain the difference between abstract class and interface in Java.",
        answer:
          "Abstract classes can have constructors and non-abstract methods, while interfaces cannot have constructors and all methods are abstract.",
      },
      {
        question: "What is a functional interface in Java 8?",
        answer:
          "A functional interface is an interface with only one abstract method. It can have multiple default and static methods.",
      },
      {
        question: "What are the new features introduced in Java 9?",
        answer:
          "Some of the new features in Java 9 include JShell (Java REPL), modules system (Project Jigsaw), and various enhancements in the API.",
      },
      {
        question: "Explain the use of the 'var' keyword introduced in Java 10.",
        answer:
          "The 'var' keyword allows for type inference in local variable declarations, reducing the need for explicit type declarations.",
      },
      {
        question:
          "What is the difference between method overloading and method overriding in Java?",
        answer:
          "Method overloading involves multiple methods in the same class with the same name but different parameters, while method overriding involves a method in a subclass with the same name and parameters as a method in the superclass.",
      },
      {
        question: "What is the purpose of a Java annotation?",
        answer:
          "Annotations provide metadata about the code and can be used for compile-time checks, runtime processing, and code generation.",
      },
      {
        question: "Explain the concept of Generics in Java.",
        answer:
          "Generics allow classes, interfaces, and methods to operate on objects of various types while providing compile-time type safety.",
      },
      {
        question:
          "What is the difference between 'synchronized' and 'volatile' in Java?",
        answer:
          "'Synchronized' ensures exclusive access to shared resources by allowing only one thread at a time to execute a synchronized block, while 'volatile' guarantees visibility of changes to variables across threads.",
      },
      {
        question: "How does garbage collection work in Java?",
        answer:
          "Garbage collection in Java automatically reclaims memory by deallocating objects that are no longer reachable.",
      },
      {
        question:
          "What is a Java stream and how is it different from a collection?",
        answer:
          "A stream is a sequence of elements that can be processed in a functional style. It differs from a collection in that it does not store elements.",
      },
      {
        question: "Explain the use of 'Optional' class in Java.",
        answer:
          "'Optional' is a container object that may or may not contain a non-null value. It helps in handling null values more effectively and avoiding NullPointerExceptions.",
      },
      {
        question:
          "What is the difference between 'HashMap' and 'ConcurrentHashMap'?",
        answer:
          "'HashMap' is not thread-safe and allows null keys and values, while 'ConcurrentHashMap' is thread-safe and does not allow null keys or values.",
      },
      {
        question: "How can you create a custom exception in Java?",
        answer:
          "To create a custom exception in Java, you need to extend the 'Exception' class or one of its subclasses.",
      },
      {
        question: "What is the difference between 'this' and 'super' in Java?",
        answer:
          "'this' refers to the current instance of a class, while 'super' refers to the superclass of the current instance.",
      },
      {
        question:
          "Explain the difference between '==', 'equals()', and 'hashCode()' methods in Java.",
        answer:
          "'==' is used for reference comparison, 'equals()' is used for content comparison, and 'hashCode()' returns the hash code value for an object.",
      },
      {
        question: "What is the purpose of the 'finalize()' method in Java?",
        answer:
          "The 'finalize()' method is called by the garbage collector before reclaiming an object's memory. It can be overridden to release resources or perform cleanup operations.",
      },
      {
        question: "How does the 'try-with-resources' statement work in Java?",
        answer:
          "The 'try-with-resources' statement automatically closes resources like streams, files, or database connections at the end of the block, ensuring proper resource management.",
      },
      {
        question: "Explain the use of 'ReentrantLock' in Java concurrency.",
        answer:
          "'ReentrantLock' is a lock implementation that provides the same basic behavior as the 'synchronized' keyword but with additional features like timeouts and interruptible lock acquisition.",
      },
      {
        question:
          "What are the benefits of using Java's 'CompletableFuture' for asynchronous programming?",
        answer:
          "'CompletableFuture' provides a simple and flexible way to perform asynchronous operations and compose them sequentially or in parallel, making it easier to write non-blocking, asynchronous code in Java.",
      },
    ],
    intermediate: [
      {
        question: "What is Java?",
        answer:
          "Java is a high-level, class-based, object-oriented programming language.",
      },
      {
        question: "What are the main features of Java?",
        answer:
          "Features like platform independence, object-oriented, robust, secure, portable, multi-threaded, and high-performance.",
      },
      {
        question: "Explain the concept of platform independence in Java.",
        answer:
          "Java code can run on any platform that has a Java Virtual Machine (JVM) without needing to be recompiled.",
      },
      {
        question: "What is the difference between JDK and JRE?",
        answer:
          "JDK (Java Development Kit) is for developing Java applications, while JRE (Java Runtime Environment) is for running Java applications.",
      },
      {
        question:
          "What is the difference between abstract class and interface?",
        answer:
          "An abstract class can have both abstract and concrete methods, while an interface can only have abstract methods.",
      },
      {
        question: "What is the difference between == and equals() method?",
        answer:
          "== is used to compare reference equality, while equals() method is used to compare object content equality.",
      },
      {
        question: "Explain the concept of polymorphism in Java.",
        answer:
          "Polymorphism allows objects to be treated as instances of their superclass, allowing for methods to be called on objects without needing to know their specific type.",
      },
      {
        question: "What is the use of the 'final' keyword in Java?",
        answer:
          "The 'final' keyword is used to declare constants, make methods not overrideable, and make classes not inheritable.",
      },
      {
        question: "What is exception handling in Java?",
        answer:
          "Exception handling is the process of handling runtime errors to prevent program termination and provide a graceful way to handle errors.",
      },
      {
        question: "What is the purpose of the 'this' keyword in Java?",
        answer:
          "The 'this' keyword refers to the current object instance and can be used to differentiate between instance variables and local variables.",
      },
      {
        question:
          "Explain the difference between checked and unchecked exceptions in Java.",
        answer:
          "Checked exceptions are checked at compile time, while unchecked exceptions are not checked by the compiler.",
      },
      {
        question: "What is the role of the 'static' keyword in Java?",
        answer:
          "The 'static' keyword is used to create class-level variables and methods that belong to the class itself rather than an instance of the class.",
      },
      {
        question: "What is method overloading in Java?",
        answer:
          "Method overloading allows a class to have multiple methods with the same name but different parameters.",
      },
      {
        question: "What is the Java Virtual Machine (JVM)?",
        answer:
          "The JVM is an abstract computing machine that provides the runtime environment in which Java bytecode can be executed.",
      },
      {
        question: "What is serialization in Java?",
        answer:
          "Serialization is the process of converting an object into a stream of bytes to store or transmit the object's state.",
      },
      {
        question: "What is the 'super' keyword used for in Java?",
        answer:
          "The 'super' keyword is used to refer to the immediate parent class of a subclass.",
      },
      {
        question: "Explain the importance of garbage collection in Java.",
        answer:
          "Garbage collection automatically reclaims memory occupied by objects that are no longer in use, preventing memory leaks.",
      },
      {
        question: "What is the purpose of the 'volatile' keyword in Java?",
        answer:
          "The 'volatile' keyword is used to indicate that a variable's value will be modified by different threads.",
      },
      {
        question: "What is the role of the 'synchronized' keyword in Java?",
        answer:
          "The 'synchronized' keyword is used to control access to critical sections of code by allowing only one thread to execute the synchronized code block at a time.",
      },
      {
        question: "How do you handle concurrency in Java?",
        answer:
          "Concurrency can be handled in Java using synchronized blocks, locks, atomic variables, and concurrent data structures from the java.util.concurrent package.",
      },
    ],
    basic: [
      {
        question: "What is Java?",
        answer:
          "A high-level programming language developed by Sun Microsystems.",
      },
      {
        question: "What is the main feature of Java?",
        answer: "Platform independence.",
      },
      {
        question: "What is the JVM?",
        answer:
          "Java Virtual Machine - the execution environment for Java programs.",
      },
      {
        question: "What is the JDK?",
        answer:
          "Java Development Kit - a software development kit for building Java applications.",
      },
      {
        question: "What is a class in Java?",
        answer: "A blueprint for creating objects.",
      },
      {
        question: "What is an object in Java?",
        answer: "An instance of a class.",
      },
      {
        question: "What is a constructor in Java?",
        answer: "A special method used to initialize objects.",
      },
      {
        question: "What is inheritance in Java?",
        answer:
          "The ability for a class to inherit properties and behavior from another class.",
      },
      {
        question: "What is polymorphism in Java?",
        answer:
          "The ability for objects of different classes to be treated as objects of a common superclass.",
      },
      {
        question: "What is encapsulation in Java?",
        answer:
          "The bundling of data and methods that operate on the data into a single unit.",
      },
      {
        question: "What is a method in Java?",
        answer: "A collection of statements that perform a specific task.",
      },
      {
        question: "What is the syntax for writing a main method in Java?",
        answer: "public static void main(String[] args) { }",
      },
      {
        question: "What is the difference between == and equals() in Java?",
        answer:
          "== is used to compare object references, while equals() is used to compare the contents of objects.",
      },
      {
        question: "What is a package in Java?",
        answer:
          "A mechanism to organize classes and interfaces into namespaces.",
      },
      {
        question:
          "What is the difference between abstract class and interface in Java?",
        answer:
          "An abstract class can have abstract and non-abstract methods, while an interface can only have abstract methods.",
      },
      {
        question: "What is the purpose of the static keyword in Java?",
        answer: "To create class-level variables and methods.",
      },
      {
        question: "What is a loop in Java?",
        answer:
          "A control flow statement that allows code to be executed repeatedly.",
      },
      {
        question: "What is the purpose of the final keyword in Java?",
        answer: "To make a variable, method, or class immutable.",
      },
      {
        question: "What is a Java interface?",
        answer: "A collection of abstract methods and constants.",
      },
      {
        question: "What is a constructor in Java?",
        answer: "A special method used to initialize objects.",
      },
    ],
  };

  const handleAccordionChange = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setCurrentPage(1);
    setExpandedIndex(null);
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
    setExpandedIndex(null);
  };

  const filteredQuestions =
    staticQuestions[selectedTab]?.filter((item) =>
      item.question.toLowerCase().includes(searchText.toLowerCase())
    ) || [];

  const currentQuestions = filteredQuestions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        py: { xs: 6, md: 8, lg: 7 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
        {/* Section Title */}
        <Typography
          sx={{
            color: "#fff",
            fontFamily: "'SF Pro Display', 'Inter', sans-serif",
            fontWeight: 600,
            fontStyle: "bold",
            fontSize: { xs: "28px", sm: "36px", md: "44px", lg: "56px" },
            lineHeight: { xs: "35px", sm: "45px", md: "55px", lg: "70px" },
            textAlign: "center",
            mb: { xs: 4, md: 6 },
          }}
        >
          Top Interview Questions
        </Typography>

        {/* Search Bar */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search Question"
            variant="outlined"
            value={searchText}
            onChange={handleSearch}
            sx={{
              maxWidth: "600px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FFC218",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff",
                "&::placeholder": {
                  color: "rgba(255, 255, 255, 0.7)",
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs */}
        <TabContext value={selectedTab}>
          <TabList
            value={selectedTab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: {
                backgroundColor: "#FFC218",
                height: "3px",
              },
            }}
            sx={{
              mb: 4,
              "& .MuiTabs-flexContainer": {
                justifyContent: "center",
                gap: 2,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 600,
                color: "rgba(255, 255, 255, 0.7)",
                minWidth: "120px",
                "&.Mui-selected": {
                  color: "#FFC218",
                },
              },
            }}
          >
            <Tab label="Advanced" value="advanced" />
            <Tab label="Intermediate" value="intermediate" />
            <Tab label="Basic" value="basic" />
          </TabList>

          <TabPanel value="advanced" sx={{ p: 0 }}>
            <Box>
              {currentQuestions.map((item, index) => (
                <Accordion
                  key={index}
                  expanded={expandedIndex === index}
                  onChange={() => handleAccordionChange(index)}
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#FFC218" }} />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        margin: "16px 0",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "16px", sm: "18px" },
                        fontWeight: 600,
                        color: "#fff",
                        fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                    <Typography
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        fontSize: { xs: "14px", sm: "16px" },
                        lineHeight: 1.6,
                        fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </TabPanel>

          <TabPanel value="intermediate" sx={{ p: 0 }}>
            <Box>
              {currentQuestions.map((item, index) => (
                <Accordion
                  key={index}
                  expanded={expandedIndex === index}
                  onChange={() => handleAccordionChange(index)}
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#FFC218" }} />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        margin: "16px 0",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "16px", sm: "18px" },
                        fontWeight: 600,
                        color: "#fff",
                        fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                    <Typography
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        fontSize: { xs: "14px", sm: "16px" },
                        lineHeight: 1.6,
                        fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </TabPanel>

          <TabPanel value="basic" sx={{ p: 0 }}>
            <Box>
              {currentQuestions.map((item, index) => (
                <Accordion
                  key={index}
                  expanded={expandedIndex === index}
                  onChange={() => handleAccordionChange(index)}
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#FFC218" }} />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        margin: "16px 0",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "16px", sm: "18px" },
                        fontWeight: 600,
                        color: "#fff",
                        fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                    <Typography
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        fontSize: { xs: "14px", sm: "16px" },
                        lineHeight: 1.6,
                        fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </TabPanel>
        </TabContext>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              px: { xs: 0, sm: 4 },
            }}
          >
            <Button
              variant="outlined"
              disabled={currentPage === 1}
              startIcon={<ChevronLeftIcon />}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              sx={{
                color: "#FFC218",
                borderColor: "#FFC218",
                "&:hover": {
                  borderColor: "#FFC218",
                  backgroundColor: "rgba(255, 194, 24, 0.1)",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Preview
            </Button>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontFamily: "'SF Pro Display', 'Inter', sans-serif",
              }}
            >
              Page {currentPage} of {totalPages}
            </Typography>
            <Button
              variant="outlined"
              disabled={currentPage === totalPages}
              endIcon={<ChevronRightIcon />}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              sx={{
                color: "#FFC218",
                borderColor: "#FFC218",
                "&:hover": {
                  borderColor: "#FFC218",
                  backgroundColor: "rgba(255, 194, 24, 0.1)",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InterviewQuestionSection;
